import { z } from "zod";

import { CalcDecimal } from "./decimal.js";

export const commissionerKindSchema = z.enum([
  "private_individual",
  "private_enterprise",
  "bank",
  "insurance_company",
  "bank_or_insurance_controlled_or_mandated",
  "public_administration",
  "tusp_company",
  // Legacy value kept readable for already saved local drafts.
  "publicly_controlled_company",
  "securitisation_vehicle",
  "collection_agent",
  "unknown",
]);

export const serviceContextSchema = z.enum([
  "public_engineering_architecture_award",
  "other_professional_engagement",
  "unknown",
]);

export const publicAwardProcedureSchema = z.enum([
  "article_108_2_b_best_value",
  "article_50_1_b_direct_award",
  "other_or_unknown",
  "not_applicable",
]);

const employeeCountSchema = z
  .string()
  .regex(/^(0|[1-9]\d{0,8})$/, "Employee count must be a non-negative integer string");

const revenueSchema = z
  .string()
  .regex(
    /^(0|[1-9]\d{0,17})(\.\d{1,2})?$/,
    "Revenue must be a non-negative decimal string with at most 2 decimal places",
  );

export const equoCompensoScreeningInputSchema = z
  .object({
    commissionerKind: commissionerKindSchema,
    serviceContext: serviceContextSchema,
    publicAwardProcedure: publicAwardProcedureSchema,
    previousYearEmployees: employeeCountSchema.optional(),
    previousYearRevenueEur: revenueSchema.optional(),
  })
  .strict();

export type CommissionerKind = z.infer<typeof commissionerKindSchema>;
export type ServiceContext = z.infer<typeof serviceContextSchema>;
export type PublicAwardProcedure = z.infer<typeof publicAwardProcedureSchema>;
export type EquoCompensoScreeningInput = z.infer<
  typeof equoCompensoScreeningInputSchema
>;

export type EquoCompensoApplicability =
  | "applies"
  | "does_not_apply"
  | "indeterminate"
  | "special_adequacy_rule";

export type EquoCompensoRoute =
  | "public_contracts_annex_i13"
  | "dm140_protected_client_review"
  | "ordinary_negotiated_estimate"
  | "collection_agent_adequacy_review"
  | "blocked";

export type EquoCompensoEstimateUse =
  | "public_procurement_parameter_base"
  | "protected_client_parameter_review"
  | "voluntary_parameter_reference"
  | "special_adequacy_review"
  | "reference_pending_classification";

export type EquoCompensoAllowedEngine =
  | "dm2016_annex_i13"
  | "dm140_pending"
  | "ordinary_negotiated"
  | "none";

export interface EquoCompensoEngineRoutingDecision {
  readonly engine: EquoCompensoAllowedEngine;
  readonly calculationAllowed: boolean;
  readonly professionalDocumentAllowed: boolean;
  readonly reason: string;
}

export const EQUO_COMPENSO_SCREENING_VERSION = "1.1.0";

export interface EquoCompensoScreeningResult {
  readonly classificationVersion: typeof EQUO_COMPENSO_SCREENING_VERSION;
  readonly checkedAsOf: "2026-08-15";
  readonly applicability: EquoCompensoApplicability;
  readonly route: EquoCompensoRoute;
  readonly estimateUse: EquoCompensoEstimateUse;
  readonly certifiedEquoCompensoAllowed: false;
  readonly reasons: readonly string[];
  readonly blockers: readonly string[];
  readonly publicProcurementRules: {
    readonly fixedPriceSharePercent: "65" | null;
    readonly discountableSharePercent: "35" | null;
    readonly directAwardMaxReductionPercent: "20" | null;
  };
  readonly sourceReferences: readonly string[];
}

export class EquoCompensoInputError extends Error {
  readonly issues: z.core.$ZodIssue[];

  constructor(issues: z.core.$ZodIssue[]) {
    super("Equo compenso screening input is invalid");
    this.name = "EquoCompensoInputError";
    this.issues = issues;
  }
}

const LARGE_ENTERPRISE_EMPLOYEE_THRESHOLD = 50;
const LARGE_ENTERPRISE_REVENUE_THRESHOLD = new CalcDecimal("10000000");

const LAW_49_ARTICLE_2 =
  "Legge 21 aprile 2023 n. 49, art. 2 (testo vigente consultato il 2026-08-15)";
const LAW_49_ARTICLE_3 =
  "Legge 21 aprile 2023 n. 49, art. 3 (testo vigente consultato il 2026-08-15)";
const PUBLIC_CODE_ARTICLE_41 =
  "D.Lgs. 31 marzo 2023 n. 36, art. 41, commi 15, 15-bis e 15-quater (testo vigente consultato il 2026-08-15)";

function frozenResult(
  result: Omit<
    EquoCompensoScreeningResult,
    "classificationVersion" | "checkedAsOf" | "estimateUse"
    | "certifiedEquoCompensoAllowed"
  >,
): EquoCompensoScreeningResult {
  const estimateUse: EquoCompensoEstimateUse = ({
    public_contracts_annex_i13: "public_procurement_parameter_base",
    dm140_protected_client_review: "protected_client_parameter_review",
    ordinary_negotiated_estimate: "voluntary_parameter_reference",
    collection_agent_adequacy_review: "special_adequacy_review",
    blocked: "reference_pending_classification",
  } as const)[result.route];
  const complete: EquoCompensoScreeningResult = {
    classificationVersion: EQUO_COMPENSO_SCREENING_VERSION,
    checkedAsOf: "2026-08-15" as const,
    estimateUse,
    certifiedEquoCompensoAllowed: false as const,
    ...result,
  };
  Object.freeze(complete.reasons);
  Object.freeze(complete.blockers);
  Object.freeze(complete.publicProcurementRules);
  Object.freeze(complete.sourceReferences);
  return Object.freeze(complete);
}

function noPublicProcurementRules(): EquoCompensoScreeningResult["publicProcurementRules"] {
  return {
    fixedPriceSharePercent: null,
    discountableSharePercent: null,
    directAwardMaxReductionPercent: null,
  };
}

function classifyPrivateEnterprise(
  input: EquoCompensoScreeningInput,
): Pick<EquoCompensoScreeningResult, "applicability" | "reasons" | "blockers"> {
  const employees = input.previousYearEmployees === undefined
    ? undefined
    : Number.parseInt(input.previousYearEmployees, 10);
  const revenue = input.previousYearRevenueEur === undefined
    ? undefined
    : new CalcDecimal(input.previousYearRevenueEur);

  if (
    (employees !== undefined && employees > LARGE_ENTERPRISE_EMPLOYEE_THRESHOLD)
    || (revenue !== undefined && revenue.gt(LARGE_ENTERPRISE_REVENUE_THRESHOLD))
  ) {
    return {
      applicability: "applies",
      reasons: [
        "Il committente supera almeno una delle soglie dell'anno precedente previste dall'art. 2 della Legge 49/2023.",
      ],
      blockers: [],
    };
  }

  if (employees !== undefined && revenue !== undefined) {
    return {
      applicability: "does_not_apply",
      reasons: [
        "Il committente non supera né 50 dipendenti né EUR 10 milioni di ricavi nell'anno precedente.",
      ],
      blockers: [],
    };
  }

  return {
    applicability: "indeterminate",
    reasons: [],
    blockers: [
      "Per una impresa privata servono dipendenti e ricavi dell'anno precedente, salvo che una delle due soglie sia già certamente superata.",
    ],
  };
}

export function classifyEquoCompenso(
  rawInput: unknown,
): EquoCompensoScreeningResult {
  const parsed = equoCompensoScreeningInputSchema.safeParse(rawInput);
  if (!parsed.success) {
    throw new EquoCompensoInputError(parsed.error.issues);
  }

  const input = parsed.data;
  const publicCommissioner = input.commissionerKind === "public_administration"
    || input.commissionerKind === "tusp_company"
    || input.commissionerKind === "publicly_controlled_company";

  if (
    input.serviceContext === "public_engineering_architecture_award"
    && !publicCommissioner
  ) {
    return frozenResult({
      applicability: "indeterminate",
      route: "blocked",
      reasons: [],
      blockers: [
        "Un affidamento pubblico di servizi di ingegneria o architettura richiede una stazione appaltante o un ente concedente coerente con il caso selezionato.",
      ],
      publicProcurementRules: noPublicProcurementRules(),
      sourceReferences: [LAW_49_ARTICLE_2, PUBLIC_CODE_ARTICLE_41],
    });
  }

  if (input.commissionerKind === "securitisation_vehicle") {
    return frozenResult({
      applicability: "does_not_apply",
      route: "ordinary_negotiated_estimate",
      reasons: [
        "L'art. 2, comma 3, esclude espressamente le società veicolo di cartolarizzazione.",
      ],
      blockers: [],
      publicProcurementRules: noPublicProcurementRules(),
      sourceReferences: [LAW_49_ARTICLE_2],
    });
  }

  if (input.commissionerKind === "collection_agent") {
    return frozenResult({
      applicability: "special_adequacy_rule",
      route: "collection_agent_adequacy_review",
      reasons: [
        "La Legge 49/2023 non si applica agli agenti della riscossione, ma richiede la pattuizione di compensi adeguati all'importanza dell'opera.",
      ],
      blockers: [
        "Il caso richiede una verifica dedicata e non può essere certificato dal percorso D.M. 140/2012.",
      ],
      publicProcurementRules: noPublicProcurementRules(),
      sourceReferences: [LAW_49_ARTICLE_2],
    });
  }

  let coverage: Pick<
    EquoCompensoScreeningResult,
    "applicability" | "reasons" | "blockers"
  >;

  if (
    input.commissionerKind === "bank"
    || input.commissionerKind === "insurance_company"
    || input.commissionerKind === "bank_or_insurance_controlled_or_mandated"
    || publicCommissioner
  ) {
    coverage = {
      applicability: "applies",
      reasons: [
        "Il tipo di committente rientra nell'ambito indicato dall'art. 2 della Legge 49/2023; la verifica non certifica da sola l'equità del singolo compenso.",
      ],
      blockers: [],
    };
  } else if (input.commissionerKind === "private_enterprise") {
    coverage = classifyPrivateEnterprise(input);
  } else if (input.commissionerKind === "private_individual") {
    coverage = {
      applicability: "does_not_apply",
      reasons: [
        "Il cliente privato persona fisica non rientra nell'elenco dei committenti dell'art. 2 della Legge 49/2023.",
      ],
      blockers: [],
    };
  } else {
    coverage = {
      applicability: "indeterminate",
      reasons: [],
      blockers: ["Selezionare il tipo di committente."],
    };
  }

  if (coverage.applicability === "indeterminate") {
    return frozenResult({
      ...coverage,
      route: "blocked",
      publicProcurementRules: noPublicProcurementRules(),
      sourceReferences: [LAW_49_ARTICLE_2],
    });
  }

  if (coverage.applicability === "does_not_apply") {
    return frozenResult({
      ...coverage,
      route: "ordinary_negotiated_estimate",
      publicProcurementRules: noPublicProcurementRules(),
      sourceReferences: [LAW_49_ARTICLE_2],
    });
  }

  if (
    publicCommissioner
    && input.serviceContext === "public_engineering_architecture_award"
  ) {
    const procedureBlockers: string[] = [];
    let publicProcurementRules = noPublicProcurementRules();

    if (input.publicAwardProcedure === "article_108_2_b_best_value") {
      publicProcurementRules = {
        fixedPriceSharePercent: "65",
        discountableSharePercent: "35",
        directAwardMaxReductionPercent: null,
      };
    } else if (input.publicAwardProcedure === "article_50_1_b_direct_award") {
      publicProcurementRules = {
        fixedPriceSharePercent: null,
        discountableSharePercent: null,
        directAwardMaxReductionPercent: "20",
      };
    } else {
      procedureBlockers.push(
        "Specificare la procedura di affidamento per determinare il trattamento economico applicabile.",
      );
    }

    return frozenResult({
      applicability: "applies",
      route: "public_contracts_annex_i13",
      reasons: [
        ...coverage.reasons,
        "La base dei corrispettivi per i servizi di ingegneria e architettura pubblici segue l'art. 41 e l'Allegato I.13 del D.Lgs. 36/2023.",
      ],
      blockers: procedureBlockers,
      publicProcurementRules,
      sourceReferences: [LAW_49_ARTICLE_2, PUBLIC_CODE_ARTICLE_41],
    });
  }

  if (input.serviceContext === "unknown") {
    return frozenResult({
      applicability: "applies",
      route: "blocked",
      reasons: coverage.reasons,
      blockers: ["Specificare se si tratta di un affidamento pubblico di servizi tecnici."],
      publicProcurementRules: noPublicProcurementRules(),
      sourceReferences: [LAW_49_ARTICLE_2, LAW_49_ARTICLE_3],
    });
  }

  return frozenResult({
    applicability: "applies",
    route: "dm140_protected_client_review",
    reasons: [
      ...coverage.reasons,
      "Il confronto economico richiede il ruleset tecnico ministeriale approvato; il corpus D.M. 140/2012 resta bloccato finché non supera la seconda verifica.",
    ],
    blockers: [
      "Il risultato numerico non può essere certificato finché il ruleset tecnico non è approvato.",
    ],
    publicProcurementRules: noPublicProcurementRules(),
    sourceReferences: [LAW_49_ARTICLE_2, LAW_49_ARTICLE_3],
  });
}

export function resolveEquoCompensoEngine(
  screening: EquoCompensoScreeningResult,
): EquoCompensoEngineRoutingDecision {
  if (screening.route === "public_contracts_annex_i13") {
    const allowed = screening.blockers.length === 0;
    return Object.freeze({
      engine: allowed ? "dm2016_annex_i13" : "none",
      calculationAllowed: allowed,
      professionalDocumentAllowed: allowed,
      reason: allowed
        ? "Affidamento pubblico di ingegneria/architettura instradato al D.M. 17 giugno 2016 e Allegato I.13."
        : "La procedura pubblica non è determinata: il motore D.M. 2016 resta bloccato.",
    });
  }
  if (screening.route === "dm140_protected_client_review") {
    return Object.freeze({
      engine: "dm140_pending",
      calculationAllowed: false,
      professionalDocumentAllowed: false,
      reason: "Percorso D.M. 140 in completamento: il D.M. 2016 non può essere usato come sostituto.",
    });
  }
  if (screening.route === "ordinary_negotiated_estimate") {
    return Object.freeze({
      engine: "ordinary_negotiated",
      calculationAllowed: false,
      professionalDocumentAllowed: false,
      reason: "Percorso ordinario negoziato, separato dal calcolo D.M. 2016 e senza certificazione Legge 49/2023.",
    });
  }
  return Object.freeze({
    engine: "none",
    calculationAllowed: false,
    professionalDocumentAllowed: false,
    reason: "Classificazione non determinata o percorso speciale: nessun motore parametrico è autorizzato.",
  });
}
